// ----------------------------------------------------------------------------
// SavedContractsView — bookmark list backed by the shared SavedContracts context
// so adds/removes from the drawer stay in sync with this page.
// ----------------------------------------------------------------------------

import React from "react";
import { Bookmark, Building2, CalendarDays, Trash2, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { SAVED } from "../../constants/testIds";
import { useSavedContracts } from "../../context/SavedContractsContext";

export const SavedContractsView = () => {
  const { items, removeById } = useSavedContracts();

  const handleRemove = (id) => {
    removeById(id);
    toast.success("Contract removed from saved list");
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-sky-700 font-semibold">
          Bookmarked · Vendor Workspace
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
          Saved Contracts
        </h1>
        <p className="text-slate-500 text-sm sm:text-base">
          Solicitations your team is actively pursuing or pre-qualifying for.
        </p>
      </header>

      {items.length === 0 ? (
        <div
          data-testid={SAVED.empty}
          className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center"
        >
          <FileSearch
            size={28}
            strokeWidth={1.5}
            className="mx-auto text-slate-400 mb-3"
          />
          <p className="text-sm font-medium text-slate-700">No saved contracts</p>
          <p className="text-xs text-slate-500 mt-1">
            Open a bid from the Active Bids ledger and click "Save Contract" to track it here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map((c) => (
            <article
              key={c.id}
              data-testid={SAVED.card(c.id)}
              className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Bookmark
                      size={14}
                      strokeWidth={1.7}
                      className="text-sky-600 fill-sky-100"
                    />
                    <code className="font-mono text-[12px] text-slate-700 bg-slate-100/80 px-1.5 py-0.5 rounded">
                      {c.id}
                    </code>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 leading-snug">
                    {c.title}
                  </h3>
                </div>
              </div>

              <div className="mt-4 space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Building2 size={13} strokeWidth={1.7} className="text-slate-400" />
                  {c.agency}
                </div>
                <div className="flex items-center gap-2">
                  <CalendarDays size={13} strokeWidth={1.7} className="text-slate-400" />
                  Saved on{" "}
                  {new Date(c.savedOn + "T00:00:00").toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>

              {c.note && (
                <p className="mt-4 text-sm text-slate-600 bg-slate-50/70 border border-slate-100 rounded-md p-3 italic">
                  “{c.note}”
                </p>
              )}

              <div className="mt-5 flex items-center justify-end">
                <button
                  data-testid={SAVED.remove(c.id)}
                  onClick={() => handleRemove(c.id)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-3 py-1.5 rounded-md transition-colors"
                >
                  <Trash2 size={14} strokeWidth={1.7} />
                  Remove
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
